import {
  Component,
  computed,
  forwardRef,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { LucideIconComponent } from '../../lucide-icon/lucide-icon.component';
import { NgxMaskDirective } from 'ngx-mask';
import { CustomFormControl } from '../custom-form-control';

@Component({
  selector: 'app-number-input',
  standalone: true,
  imports: [LucideIconComponent, NgxMaskDirective, FormsModule],
  templateUrl: './number-input.component.html',
  styleUrls: ['./number-input.component.css'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberInputComponent),
      multi: true,
    },
    {
      provide: CustomFormControl,
      useExisting: forwardRef(() => NumberInputComponent),
    },
  ],
})
export class NumberInputComponent
  implements ControlValueAccessor, CustomFormControl
{
  // --- Constants ---
  protected readonly POSITIVE_INFINITY = Number.POSITIVE_INFINITY;
  protected readonly NEGATIVE_INFINITY = Number.NEGATIVE_INFINITY;

  // --- Inputs ---
  mode = input<'decimal' | 'integer'>('decimal');
  currency = input<'USD' | null>(null);
  showGrouping = input(false);
  min = input<number>(-Infinity);
  // FIX: Default max is now Infinity. A specific limit will be applied conditionally.
  max = input<number>(Infinity);
  step = input(1);
  size = input<'sm' | 'md' | 'lg'>('md');
  variant = input<'outline' | 'ghost' | 'blend'>('outline');
  layout = input<'horizontal' | 'vertical'>('horizontal');
  placeholder = input('0');
  prefixText = input<string>();
  prefixIcon = input<string>();
  suffixText = input<string>();
  suffixIcon = input<string>();
  showStepButtons = input(false);
  isNegative = input(false);
  class = input<string>('');

  // --- CVA State ---
  internalValue = signal<number | null>(null);
  disabled = signal(false);
  isInvalid = signal(false);
  onChange: (value: number | null) => void = () => {};
  onTouched: () => void = () => {};

  // --- MASK & DERIVED STATE ---
  /** The effective maximum value, applying a limit only for the vertical stepper. */
  readonly effectiveMax = computed(() => {
    if (this.layout() === 'vertical' && this.max() === Infinity) {
      return 999999;
    }
    return this.max();
  });

  readonly mask = computed(() => {
    if (this.currency() === 'USD') return 'separator.2';
    if (this.mode() === 'integer') return 'separator.0';
    return 'separator'; // Default decimal mask
  });

  readonly thousandSeparator = computed(() => {
    return this.currency() === 'USD' || this.showGrouping() ? ',' : '';
  });

  readonly prefix = computed(() => {
    return this.currency() === 'USD' ? '$ ' : '';
  });

  // --- DYNAMIC CLASSES ---
  readonly containerClasses = computed(() => {
    const classes = [
      'input-base',
      `input-variant-${this.variant()}`,
      `input-size-${this.size()}`,
      this.class(),
    ];
    if (this.disabled()) classes.push('opacity-60 cursor-not-allowed');
    if (this.isInvalid()) classes.push('input-error');
    if (this.showStepButtons() && this.layout() === 'horizontal')
      classes.push('has-stepper-h');
    if (this.showStepButtons() && this.layout() === 'vertical')
      classes.push('has-stepper-v');
    return classes.join(' ');
  });

  // --- CVA IMPLEMENTATION ---
  writeValue(value: number | null): void {
    this.internalValue.set(this.parseToNumber(value));
  }
  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // --- EVENT HANDLERS ---
  onBlur(): void {
    this.onTouched();
  }

  onValueChange(value: string): void {
    this.updateValue(this.parseToNumber(value));
  }

  stepUp(): void {
    const current = this.internalValue() ?? 0;
    this.updateValue(current + this.step());
  }

  stepDown(): void {
    const current = this.internalValue() ?? 0;
    this.updateValue(current - this.step());
  }

  // --- HELPERS ---
  private parseToNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(String(value).replace(/[^0-9.-]/g, ''));
    return isNaN(num) ? null : num;
  }

  private updateValue(newValue: number | null): void {
    if (newValue === this.internalValue()) return;
    let clampedValue = newValue;
    if (clampedValue !== null) {
      // FIX: Use the new effectiveMax for clamping.
      clampedValue = Math.max(
        this.min(),
        Math.min(this.effectiveMax(), clampedValue)
      );
    }
    this.internalValue.set(clampedValue);
    this.onChange(clampedValue);
  }
}
